import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { clientStore } from 'apps/client/app';
import { NavCubeConfig } from 'libs/rendering/gltf/config';
import { CameraPanel } from 'libs/rendering/gltf/components/panel/cameraPanel';
import Tooltip from 'components/Tooltip';
import {
  CADComparisonHeight,
  FrameHeaderHeight,
  CameraSettingWrapperWidth,
  ConflictAlertHeight,
} from 'styles/constants';
import { useRouterStore } from 'apps/hooks';

import {
  NavCubeStyled,
  ButtonStyled,
  Separator,
  StdViewWrapper,
} from './navCube.styled';
import { useNavCube } from './useNavCube';

const NavCube = observer(props => {
  const {
    renderMap,
    viewerControl,
    toggleUpAxis,
    upAxisOptions,
    renderOptions,
    cameraOptions,
    stdLabel,
    stdLabelHigh,
  } = useNavCube();

  const [isExpanded, setIsExpanded] = useState(false);

  const routerStore = useRouterStore();
  const { name: routeName, params = {} } = routerStore.route;

  const { diff, repoId } = params;
  const { shouldShowCameraPanel } = viewerControl;

  const viewer = viewerControl?.viewer;
  const { settings, navCube } = viewer || {};
  const camera = settings?.CurrentCamera();
  const isDiffing = camera && viewer?.isDiffing;
  const tabLoadingStatus = camera?.GetLoadStatus();
  const diffingStatus = isDiffing && !viewer?.diffTool?.isSuperposed;

  const toggleCameraPanel = () => {
    routerStore.navigate(
      routeName,
      { ...params, panel: shouldShowCameraPanel ? null : 'camera' },
      { replace: true },
    );
  };

  const AxiosHeight = 98;
  const hasConflict = diff && repoId;
  const conflictHeight = hasConflict ? ConflictAlertHeight : 0;
  const comparisonHeight = diffingStatus ? CADComparisonHeight : 0;

  const extraHeight = conflictHeight + comparisonHeight;

  const navCubeOffsetY =
    clientStore.windowHeight -
    clientStore.PageHeaderHeight -
    AxiosHeight -
    extraHeight;

  // update navCube position
  useEffect(() => {
    if (navCube && viewerControl.viewerReady) {
      navCube.SetOffsetY(navCubeOffsetY);
      navCube.SetOffsetX(NavCubeConfig.offsetX);
    }
  }, [navCube, navCubeOffsetY, viewerControl.viewerReady]);

  if (!viewerControl?.viewerVisible) return null;

  const style = {
    minWidth: CameraSettingWrapperWidth / 2 - 1,
    height: 32,
  };

  const shouldShowOutlineStyle = !clientStore.isMobile && upAxisOptions;

  const hasOptions = renderOptions || cameraOptions;

  const renderMode = isExpanded && (
    <div
      data-testid='CADPage-operateMode'
      className=' outline outline-1 outline-wfGrey100 bg-wfWhite'
      style={{
        width: 'inherit',
      }}
    >
      {renderMap.camera}
      {renderMap.render}
      {renderMap.orbit}
    </div>
  );

  return (
    <>
      <div
        data-testid='CADPage-navCube'
        className={
          shouldShowOutlineStyle
            ? `absolute h-fit border-l border-wfGrey100 right-0 w-[110px] mt-[1px]`
            : ''
        }
        style={{
          right: props.right,
          top: extraHeight,
          width: CameraSettingWrapperWidth,
        }}
      >
        <div style={{ height: AxiosHeight }} />
        {camera && tabLoadingStatus === 0 && (
          <NavCubeStyled
            className={
              shouldShowOutlineStyle ? 'border border-r-0 border-wfGrey100' : ''
            }
            width={CameraSettingWrapperWidth}
            height={FrameHeaderHeight}
            top={AxiosHeight}
            background='transparent'
            expanded={isExpanded}
            style={{
              width: CameraSettingWrapperWidth,
            }}
          >
            {stdLabel?.length > 0 && (
              <StdViewWrapper
                data-testid='CADPage-DirectionLabel'
                width={NavCubeConfig.size}
                height={NavCubeConfig.size + NavCubeConfig.offsetY}
                isHighlight={stdLabelHigh}
              >
                {stdLabel}
              </StdViewWrapper>
            )}

            <div className='w-full flex m-0 p-0'>
              <Tooltip delayShow={500} place='left' id='navCubeTooltip' />
              {cameraOptions && (
                <>
                  <ButtonStyled
                    data-intercom-target='Camera-Modes Button'
                    size='small'
                    variant='tertiary'
                    active={isExpanded}
                    data-for='navCubeTooltip'
                    data-tip='View settings'
                    data-position='left'
                    icon='camera'
                    onClick={() => {
                      setIsExpanded(!isExpanded);
                    }}
                    hasDropdown
                    removeBold
                    style={{
                      ...style,
                    }}
                    appearance='plain'
                  />
                </>
              )}

              {hasOptions && <Separator />}

              {/* Up axis */}
              {upAxisOptions && (
                <ButtonStyled
                  size='small'
                  variant='tertiary'
                  data-for='navCubeTooltip'
                  data-tip='Toggle up-axis'
                  icon={settings.IsUpZ() ? 'axis_z' : 'axis_y'}
                  iconViewBox='0 0 40 40'
                  onClick={e => toggleUpAxis(e)}
                  appearance='plain'
                  isAxis
                  style={{
                    ...style,
                    margin: 0,
                  }}
                  highligthed
                />
              )}
            </div>
            {renderMode}
          </NavCubeStyled>
        )}
      </div>
      <CameraPanel
        isOpen={shouldShowCameraPanel}
        onToggle={toggleCameraPanel}
      />
    </>
  );
});

export default NavCube;
